import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Truck, PackageCheck, Send, Thermometer, Camera, Loader2, ClipboardList } from 'lucide-react';

const STATUT_BADGE = {
  brouillon: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-700' },
  envoyee: { label: 'Envoyée', cls: 'bg-blue-100 text-blue-700' },
  recue: { label: 'Reçue', cls: 'bg-green-100 text-green-700' },
  annulee: { label: 'Annulée', cls: 'bg-red-100 text-red-700' },
};
const eur = (n) => (Number(n) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const UNITES = ['kg', 'g', 'L', 'cl', 'ml', 'unité', 'pièce', 'botte', 'barquette', 'carton', 'sachet', 'boîte', 'colis'];

export function CommandesFournisseursPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [commandes, setCommandes] = useState([]);
  const [stats, setStats] = useState({});
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produits, setProduits] = useState([]);

  // dialog commande
  const [openCmd, setOpenCmd] = useState(false);
  const [form, setForm] = useState(null);
  // dialog réception
  const [openRec, setOpenRec] = useState(false);
  const [rec, setRec] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, f, p] = await Promise.all([
        api.get('/commandes'),
        api.get('/commandes/stats'),
        api.get('/commandes/fournisseurs'),
        api.get('/mercuriale/produits').catch(() => ({ data: [] })),
      ]);
      setCommandes(c.data || []);
      setStats(s.data || {});
      setFournisseurs(f.data || []);
      setProduits(p.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // -------- création commande --------
  const newCommande = () => {
    setForm({ fournisseur: '', date_livraison_prevue: '', notes: '', lignes: [] });
    setOpenCmd(true);
  };
  const produitsFournisseur = form?.fournisseur
    ? produits.filter((p) => (p.fournisseur || '') === form.fournisseur)
    : produits;

  const addLigne = () => setForm((f) => ({ ...f, lignes: [...f.lignes, { produit_id: '', nom: '', unite: '', quantite: 1, prix_unitaire: 0 }] }));
  const setLigne = (i, patch) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], ...patch }; return { ...f, lignes: l }; });
  const removeLigne = (i) => setForm((f) => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));
  const pickProduit = (i, produitId) => {
    const p = produits.find((x) => x.id === produitId);
    if (p) setLigne(i, { produit_id: p.id, nom: p.nom, unite: p.unite || '', prix_unitaire: p.prix_unitaire || p.prix || 0 });
  };
  const totalForm = (form?.lignes || []).reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0), 0);

  const saveCommande = async () => {
    if (!form.fournisseur) return toast.error('Choisis un fournisseur');
    if (!form.lignes.length) return toast.error('Ajoute au moins une ligne');
    setSaving(true);
    try {
      await api.post('/commandes', { ...form, statut: 'envoyee' });
      toast.success('Commande créée');
      setOpenCmd(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const setStatut = async (c, statut) => {
    try { await api.put(`/commandes/${c.id}`, { statut }); load(); }
    catch { toast.error('Erreur'); }
  };
  const del = async (c) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try { await api.delete(`/commandes/${c.id}`); load(); } catch { toast.error('Erreur'); }
  };

  // -------- réception --------
  const openReception = (c) => {
    setRec({
      commande: c,
      bl_numero: '',
      temperature: '',
      bl_photo: '',
      date_reception: new Date().toISOString().slice(0, 10),
      lignes: (c.lignes || []).map((l) => ({ ...l, quantite_recue: l.quantite })),
    });
    setOpenRec(true);
  };
  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error('Photo trop lourde (max 8 Mo)');
    const reader = new FileReader();
    reader.onload = () => setRec((r) => ({ ...r, bl_photo: reader.result }));
    reader.readAsDataURL(file);
  };
  const saveReception = async () => {
    setSaving(true);
    try {
      const payload = {
        bl_numero: rec.bl_numero,
        temperature: rec.temperature,
        bl_photo: rec.bl_photo,
        date_reception: rec.date_reception,
        lignes_recues: rec.lignes.map((l) => ({ produit_id: l.produit_id, nom: l.nom, unite: l.unite, quantite_recue: Number(l.quantite_recue) || 0 })),
      };
      const res = await api.post(`/commandes/${rec.commande.id}/reception`, payload);
      toast.success(res.data?.message || 'Réception enregistrée');
      setOpenRec(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2"><Truck className="w-6 h-6 text-primary" /> Commandes fournisseurs</h1>
          <p className="text-sm text-muted-foreground">Suivi des commandes et réception (BL + température)</p>
        </div>
        <Button onClick={newCommande} data-testid="btn-new-commande"><Plus className="w-4 h-4 mr-1" /> Nouvelle commande</Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['total', 'Total'], ['envoyee', 'Envoyées'], ['recue', 'Reçues'], ['brouillon', 'Brouillons']].map(([k, lbl]) => (
          <Card key={k}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{lbl}</p>
            <p className="text-2xl font-bold tabular-nums">{stats[k] || 0}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Liste */}
      <Card><CardContent className="p-0">
        <div className="table-responsive">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Fournisseur</TableHead><TableHead>Lignes</TableHead>
              <TableHead>Total</TableHead><TableHead>Livraison</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {commandes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucune commande. Clique sur « Nouvelle commande ».</TableCell></TableRow>}
              {commandes.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="whitespace-nowrap">{c.date_commande}</TableCell>
                  <TableCell className="font-medium">{c.fournisseur}</TableCell>
                  <TableCell>{(c.lignes || []).length}</TableCell>
                  <TableCell className="tabular-nums">{eur(c.total_ht)}</TableCell>
                  <TableCell className="whitespace-nowrap">{c.date_livraison_prevue || '—'}</TableCell>
                  <TableCell><Badge className={STATUT_BADGE[c.statut]?.cls}>{STATUT_BADGE[c.statut]?.label || c.statut}</Badge>
                    {c.reception?.temperature != null && <span className="ml-1 text-xs text-muted-foreground inline-flex items-center"><Thermometer className="w-3 h-3" />{c.reception.temperature}°</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {c.statut === 'brouillon' && <Button size="sm" variant="ghost" onClick={() => setStatut(c, 'envoyee')} title="Marquer envoyée"><Send className="w-4 h-4" /></Button>}
                    {c.statut !== 'recue' && c.statut !== 'annulee' && <Button size="sm" onClick={() => openReception(c)} data-testid={`btn-recept-${c.id}`}><PackageCheck className="w-4 h-4 mr-1" />Réceptionner</Button>}
                    {c.statut !== 'recue' && <Button size="sm" variant="ghost" onClick={() => del(c)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {/* Dialog nouvelle commande */}
      <Dialog open={openCmd} onOpenChange={setOpenCmd}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nouvelle commande fournisseur</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Fournisseur</Label>
                  <select className="w-full border rounded-md h-10 px-2 bg-background" value={form.fournisseur} onChange={(e) => setForm((f) => ({ ...f, fournisseur: e.target.value, lignes: [] }))}>
                    <option value="">— Choisir —</option>
                    {fournisseurs.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div><Label>Livraison prévue</Label><Input type="date" value={form.date_livraison_prevue} onChange={(e) => setForm((f) => ({ ...f, date_livraison_prevue: e.target.value }))} /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2"><Label>Produits</Label>
                  <Button size="sm" variant="outline" onClick={addLigne} disabled={!form.fournisseur}><Plus className="w-3 h-3 mr-1" />Ligne</Button></div>
                {!form.fournisseur && <p className="text-xs text-muted-foreground">Choisis d'abord un fournisseur.</p>}
                <div className="space-y-2">
                  {form.lignes.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <select className="col-span-4 border rounded-md h-9 px-2 bg-background text-sm" value={l.produit_id} onChange={(e) => pickProduit(i, e.target.value)}>
                        <option value="">— Produit —</option>
                        {produitsFournisseur.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                      </select>
                      <Input className="col-span-2 h-9" type="number" step="0.01" value={l.quantite} onChange={(e) => setLigne(i, { quantite: e.target.value })} placeholder="Qté" />
                      <select className="col-span-2 border rounded-md h-9 px-1 bg-background text-xs" value={l.unite || ''} onChange={(e) => setLigne(i, { unite: e.target.value })}>
                        <option value="">unité…</option>
                        {UNITES.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <Input className="col-span-2 h-9" type="number" step="0.01" value={l.prix_unitaire} onChange={(e) => setLigne(i, { prix_unitaire: e.target.value })} placeholder="PU €" />
                      <span className="col-span-1 text-xs tabular-nums text-right">{eur((Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0))}</span>
                      <Button className="col-span-1" size="sm" variant="ghost" onClick={() => removeLigne(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
                {form.lignes.length > 0 && <p className="text-right font-semibold mt-2">Total : {eur(totalForm)}</p>}
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCmd(false)}>Annuler</Button>
            <Button onClick={saveCommande} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer la commande'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog réception */}
      <Dialog open={openRec} onOpenChange={setOpenRec}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-primary" /> Réception — {rec?.commande?.fournisseur}</DialogTitle></DialogHeader>
          {rec && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>N° de BL</Label><Input value={rec.bl_numero} onChange={(e) => setRec((r) => ({ ...r, bl_numero: e.target.value }))} placeholder="Ex: BL-2026-0142" data-testid="rec-bl" /></div>
                <div><Label className="flex items-center gap-1"><Thermometer className="w-3 h-3" />Température (°C)</Label><Input type="number" step="0.1" value={rec.temperature} onChange={(e) => setRec((r) => ({ ...r, temperature: e.target.value }))} placeholder="Ex: 3.5" data-testid="rec-temp" /></div>
                <div><Label>Date</Label><Input type="date" value={rec.date_reception} onChange={(e) => setRec((r) => ({ ...r, date_reception: e.target.value }))} /></div>
              </div>

              <div>
                <Label className="flex items-center gap-1"><Camera className="w-3 h-3" />Photo du BL</Label>
                <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="block w-full text-sm mt-1" data-testid="rec-photo" />
                {rec.bl_photo && <img src={rec.bl_photo} alt="BL" className="mt-2 max-h-40 rounded-md border" />}
              </div>

              <div>
                <Label>Quantités reçues</Label>
                <div className="space-y-1 mt-1">
                  {rec.lignes.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="flex-1 text-sm">{l.nom}</span>
                      <Input className="w-20 h-8" type="number" step="0.01" value={l.quantite_recue}
                        onChange={(e) => setRec((r) => { const ls = [...r.lignes]; ls[i] = { ...ls[i], quantite_recue: e.target.value }; return { ...r, lignes: ls }; })} />
                      <select className="w-20 h-8 border rounded-md px-1 bg-background text-xs" value={l.unite || ''}
                        onChange={(e) => setRec((r) => { const ls = [...r.lignes]; ls[i] = { ...ls[i], unite: e.target.value }; return { ...r, lignes: ls }; })}>
                        <option value="">unité…</option>
                        {UNITES.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenRec(false)}>Annuler</Button>
            <Button onClick={saveReception} disabled={saving} data-testid="rec-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider la réception'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
